import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ProgressRing } from '@/components/ui/progress-ring';
import { VitalsSummaryTable } from '@/components/dashboard/VitalsSummaryTable';
import { usePatientRole } from '@/hooks/usePatientRole';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Pill,
  Heart,
  Utensils,
  Droplets,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Zap,
  Dumbbell
} from 'lucide-react';
import { format, subDays, addDays, startOfDay, endOfDay, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TaskExecution {
  id: string;
  executed_at: string;
  is_alert: boolean;
  is_sporadic: boolean;
  task_id: string | null;
  task_type: string;
  task_name: string;
  adhoc_task_type?: string | null;
  adhoc_name?: string | null;
}

interface VitalExecution {
  id: string;
  executed_at: string;
  vital_value: number | null;
  vital_value_secondary: number | null;
  is_alert: boolean;
  task_name: string;
  vital_type: string | null;
  unit: string | null;
  min_alert_value: number | null;
  max_alert_value: number | null;
  notes?: string | null;
  edited_at?: string | null;
}

interface DailyStats {
  scheduledExecutions: number;
  adhocExecutions: number;
  totalExecutions: number;
  byType: Record<string, number>;
  alerts: number;
  shifts: number;
  lateTasks: number;
  adhocOther: number;
}

const taskTypeLabels: Record<string, string> = {
  fixed_medication: 'carePlan.fixedMeds',
  sporadic_medication: 'dashboard.sporadicMeds',
  vital_sign: 'carePlan.vitals',
  food: 'carePlan.food',
  hygiene: 'carePlan.hygiene',
  incident: 'dashboard.incidents',
  physical_activity: 'carePlan.physicalActivity',
  other: 'carePlan.otherTasks'
};

const taskTypeIcons: Record<string, React.ReactNode> = {
  fixed_medication: <Pill className="w-5 h-5" />,
  sporadic_medication: <Pill className="w-5 h-5" />,
  vital_sign: <Heart className="w-5 h-5" />,
  food: <Utensils className="w-5 h-5" />,
  hygiene: <Droplets className="w-5 h-5" />,
  incident: <AlertTriangle className="w-5 h-5 text-destructive" />,
  physical_activity: <Dumbbell className="w-5 h-5" />,
  other: <Activity className="w-5 h-5" />
};

export default function DailyReport() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canEdit, isPatientActive } = usePatientRole(patientId);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [executions, setExecutions] = useState<TaskExecution[]>([]);
  const [stats, setStats] = useState<DailyStats>({
    scheduledExecutions: 0,
    adhocExecutions: 0,
    totalExecutions: 0,
    byType: {},
    alerts: 0,
    shifts: 0,
    lateTasks: 0,
    adhocOther: 0
  });
  const [scheduledTaskInstances, setScheduledTaskInstances] = useState(0);
  const [vitalExecutions, setVitalExecutions] = useState<VitalExecution[]>([]);
  const [vitalHistory, setVitalHistory] = useState<Record<string, Array<{ value: number; isAlert?: boolean }>>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (patientId) {
      fetchDailyData();
    }
  }, [patientId, selectedDate]);

  const fetchVitalsData = async (dayStart: string, dayEnd: string) => {
    // Fetch vital sign executions for this day with task details
    const { data: vitalExecs } = await supabase
      .from('task_executions')
      .select('id, executed_at, vital_value, vital_value_secondary, is_alert, task_id, adhoc_task_type, adhoc_name, adhoc_vital_type, notes, edited_at')
      .eq('patient_id', patientId)
      .gte('executed_at', dayStart)
      .lte('executed_at', dayEnd)
      .not('vital_value', 'is', null)
      .order('executed_at', { ascending: true });

    if (!vitalExecs?.length) {
      setVitalExecutions([]);
      setVitalHistory({});
      return;
    }

    // Get task details for names
    const taskIds = [...new Set(vitalExecs.map(e => e.task_id).filter(Boolean))] as string[];
    let tasksMap = new Map<string, { name: string; vital_type: string | null; unit: string | null }>();
    if (taskIds.length > 0) {
      const { data: tasks } = await supabase
        .from('care_plan_tasks')
        .select('id, name, vital_type, unit')
        .in('id', taskIds);
      tasksMap = new Map(tasks?.map(t => [t.id, t]) || []);
    }

    // Fetch ranges from patient_vital_ranges
    const { data: rangesData } = await supabase
      .from('patient_vital_ranges')
      .select('vital_type, min_value, max_value, unit')
      .eq('patient_id', patientId!);
    const rangesMap = new Map((rangesData || []).map(r => [r.vital_type, r]));

    const enriched: VitalExecution[] = vitalExecs.map(e => {
      const task = e.task_id ? tasksMap.get(e.task_id) : null;
      const vType = task?.vital_type || e.adhoc_vital_type || null;
      const rangeInfo = vType ? rangesMap.get(vType as any) : null;
      return {
        id: e.id,
        executed_at: e.executed_at,
        vital_value: e.vital_value,
        vital_value_secondary: e.vital_value_secondary,
        is_alert: e.is_alert || false,
        task_name: task?.name || e.adhoc_name || 'Sinal vital',
        vital_type: vType,
        unit: rangeInfo?.unit || task?.unit || null,
        min_alert_value: rangeInfo?.min_value ?? null,
        max_alert_value: rangeInfo?.max_value ?? null,
        notes: (e as any).notes ?? null,
        edited_at: (e as any).edited_at ?? null,
      };
    });
    setVitalExecutions(enriched);

    // Fetch last 7 readings per vital type for sparklines
    const vitalTypes = [...new Set(enriched.map(v => v.vital_type).filter(Boolean))] as string[];
    const historyMap: Record<string, Array<{ value: number; isAlert?: boolean }>> = {};

    await Promise.all(vitalTypes.map(async (vType) => {
      const { data: history } = await supabase
        .from('task_executions')
        .select('vital_value, is_alert, task_id')
        .eq('patient_id', patientId!)
        .not('vital_value', 'is', null)
        .lte('executed_at', dayEnd)
        .order('executed_at', { ascending: false })
        .limit(10);

      if (history) {
        // Filter by vital type matching
        const relevant = history.filter(h => {
          if (!h.task_id) return false;
          const t = tasksMap.get(h.task_id);
          return t?.vital_type === vType;
        });
        historyMap[vType] = relevant
          .reverse()
          .map(h => ({ value: h.vital_value!, isAlert: h.is_alert || false }));
      }
    }));

    setVitalHistory(historyMap);
  };

  const fetchDailyData = async () => {
    setIsLoading(true);
    try {
      const dayStart = startOfDay(selectedDate).toISOString();
      const dayEnd = endOfDay(selectedDate).toISOString();

      // Fetch task executions for the day
      const { data: executionsData } = await supabase
        .from('task_executions')
        .select('id, executed_at, is_alert, is_sporadic, task_id, adhoc_task_type, adhoc_name')
        .eq('patient_id', patientId)
        .gte('executed_at', dayStart)
        .lte('executed_at', dayEnd)
        .order('executed_at', { ascending: true });

      if (executionsData && executionsData.length > 0) {
        // Filter out null task_ids before querying
        const taskIds = [...new Set(executionsData.map(e => e.task_id).filter(Boolean))] as string[];

        let tasksMap = new Map<string, { id: string; name: string; task_type: string }>();
        if (taskIds.length > 0) {
          const { data: tasksData } = await supabase
            .from('care_plan_tasks')
            .select('id, name, task_type')
            .in('id', taskIds);
          tasksMap = new Map(tasksData?.map(t => [t.id, t]) || []);
        }

        const enrichedExecutions: TaskExecution[] = executionsData.map(e => {
          const task = e.task_id ? tasksMap.get(e.task_id) : null;

          return {
            ...e,
            is_sporadic: e.is_sporadic || false,
            task_type: task?.task_type || e.adhoc_task_type || 'other',
            task_name: task?.name || e.adhoc_name || t('taskDefault.adhocTask')
          };
        });

        setExecutions(enrichedExecutions);

        // Calculate stats by type using standardized 7-KPI logic
        const byType: Record<string, number> = {};
        let alerts = 0;
        let scheduledCount = 0;
        let sporadicMedsCount = 0;
        let adhocOtherCount = 0;

        enrichedExecutions.forEach(exec => {
          byType[exec.task_type] = (byType[exec.task_type] || 0) + 1;
          if (exec.is_alert || exec.adhoc_task_type === 'incident') alerts++;

          if (exec.task_id && !exec.is_sporadic) {
            scheduledCount++;
          } else if (exec.adhoc_task_type === 'sporadic_medication' || (exec.task_id && exec.is_sporadic)) {
            sporadicMedsCount++;
          } else if (!exec.task_id) {
            adhocOtherCount++;
          }
        });

        setStats(prev => ({
          ...prev,
          scheduledExecutions: scheduledCount,
          adhocExecutions: sporadicMedsCount,
          adhocOther: adhocOtherCount,
          totalExecutions: enrichedExecutions.length,
          byType,
          alerts
        }));
      } else {
        setExecutions([]);
        setStats({
          scheduledExecutions: 0,
          adhocExecutions: 0,
          totalExecutions: 0,
          byType: {},
          alerts: 0,
          shifts: 0,
          lateTasks: 0,
          adhocOther: 0
        });
      }

      // Fetch shifts count for the day
      const { count: shiftsCount } = await supabase
        .from('shifts')
        .select('id', { count: 'exact', head: true })
        .eq('patient_id', patientId)
        .gte('check_in_at', dayStart)
        .lte('check_in_at', dayEnd);

      setStats(prev => ({ ...prev, shifts: shiftsCount || 0 }));

      // Fetch total scheduled task instances for the day (from shifts that started on this day)
      const { data: shiftsForDay } = await supabase
        .from('shifts')
        .select('id')
        .eq('patient_id', patientId)
        .gte('check_in_at', dayStart)
        .lte('check_in_at', dayEnd);

      if (shiftsForDay && shiftsForDay.length > 0) {
        const shiftIds = shiftsForDay.map(s => s.id);
        const { data: instancesData } = await supabase
          .from('shift_task_instances')
          .select('id, status')
          .in('shift_id', shiftIds);

        if (instancesData) {
          setScheduledTaskInstances(instancesData.length);
          const lateCount = instancesData.filter(i => i.status === 'late').length;
          setStats(prev => ({ ...prev, lateTasks: lateCount }));
        }
      } else {
        // Fallback: count scheduled tasks if no shifts
        const { count: tasksCount } = await supabase
          .from('care_plan_tasks')
          .select('id', { count: 'exact', head: true })
          .eq('patient_id', patientId)
          .eq('is_active', true)
          .eq('is_sporadic', false);

        setScheduledTaskInstances(tasksCount || 0);
      }

      // Fetch vitals data for summary table
      await fetchVitalsData(dayStart, dayEnd);

    } catch (error) {
      console.error('Error fetching daily data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const goToPreviousDay = () => {
    setSelectedDate(prev => subDays(prev, 1));
  };

  const goToNextDay = () => {
    if (!isToday(selectedDate)) {
      setSelectedDate(prev => addDays(prev, 1));
    }
  };

  const completionRate = scheduledTaskInstances > 0
    ? Math.round((stats.scheduledExecutions / scheduledTaskInstances) * 100)
    : 0;

  return (
    <AppLayout
      title={t('dailyReport.title')}
      showBack
      onBack={() => navigate(`/app/family/${patientId}`)}
    >
      <div className="p-4 space-y-4 pb-24">
        {/* Date Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPreviousDay}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              <div className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="font-semibold">
                    {isToday(selectedDate)
                      ? t('familyDashboard.today')
                      : format(selectedDate, "EEEE", { locale: ptBR })}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {format(selectedDate, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </p>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={goToNextDay}
                disabled={isToday(selectedDate)}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <CareOSLoader size="md" />
          </div>
        ) : (
          <>
            {/* Today's Stats - Standardized 7-KPI Layout */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-primary">{scheduledTaskInstances}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.plannedTasks')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-primary">{stats.scheduledExecutions}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.executedTasks')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-primary">{completionRate}%</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.completion')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className={`text-lg font-bold ${stats.adhocExecutions > 0 ? 'text-sporadic' : 'text-muted-foreground'}`}>{stats.adhocExecutions}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.sporadicMeds')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className={`text-lg font-bold ${stats.alerts > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>{stats.alerts}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.incidents')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className={`text-lg font-bold ${stats.lateTasks > 0 ? 'text-warning' : 'text-muted-foreground'}`}>{stats.lateTasks}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.lateTasks')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-muted-foreground">{stats.adhocOther}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.adhocTasks')}</p>
              </Card>
            </div>

            {/* By Type Breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('dailyReport.byCategory')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(taskTypeLabels).map(([type, label]) => {
                  const count = stats.byType[type] || 0;
                  return (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary/10 text-primary">
                          {taskTypeIcons[type]}
                        </div>
                        <span className="text-sm">{t(label)}</span>
                      </div>
                      <Badge variant={count > 0 ? 'default' : 'secondary'}>
                        {count}
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Vitals Summary */}
            <VitalsSummaryTable
              vitals={vitalExecutions}
              recentHistory={vitalHistory}
              patientId={patientId}
              canEdit={canEdit && isPatientActive}
              onChanged={fetchDailyData}
            />

            {/* Timeline */}
            {executions.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('dailyReport.timeline')}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {executions.map(exec => (
                      <div key={exec.id} className="flex items-center gap-3 p-3">
                        <div className={`p-2 rounded-full ${exec.is_alert
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-primary/10 text-primary'
                          }`}>
                          {exec.is_alert
                            ? <AlertTriangle className="w-4 h-4" />
                            : taskTypeIcons[exec.task_type]
                          }
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{exec.task_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t(taskTypeLabels[exec.task_type])}
                          </p>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(exec.executed_at), 'HH:mm')}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {executions.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('dailyReport.noTasks')}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
